import { useEffect, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { getUserId, planetGet, planetPost } from "../../api/planetApi";

const STATUS_LABEL = {
  pending_audit: "审核中",
  rejected: "未通过",
  queued: "排队匹配",
  drifting: "漂流中",
  pending_claim: "待领取",
  read: "已读",
  replied: "已回信",
  closed: "已结束",
  expired: "已过期",
  rejected_by_user: "对方拒绝",
  frozen: "已冻结",
};

export default function PlanetBottlesPage() {
  const userId = getUserId();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const role = searchParams.get("role") || "all";
  const [items, setItems] = useState([]);
  const [invites, setInvites] = useState({ incoming: [], outgoing: [] });
  const [msg, setMsg] = useState("");

  const setRole = (v) => {
    const next = new URLSearchParams(searchParams);
    if (v === "all") next.delete("role");
    else next.set("role", v);
    setSearchParams(next, { replace: true });
  };

  const load = async () => {
    if (!userId) return;
    try {
      const [data, inv] = await Promise.all([
        planetGet(`/bottles/mine?user_id=${userId}&role=${role}`),
        planetGet(`/invites?user_id=${userId}`),
      ]);
      setItems(data.items || []);
      setInvites({
        incoming: inv.incoming || [],
        outgoing: inv.outgoing || [],
      });
    } catch (e) {
      setMsg(e.message);
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userId, role]);

  const respondInvite = async (inviteId, accept) => {
    try {
      const res = await planetPost(`/invites/${inviteId}/respond`, {
        user_id: userId,
        accept,
      });
      if (accept && res.peer_user_id) {
        localStorage.setItem(
          "current_chat_user",
          JSON.stringify({ id: res.peer_user_id, name: `用户${res.peer_user_id}` })
        );
        navigate("/chat", {
          state: { user: { id: res.peer_user_id, name: `用户${res.peer_user_id}` } },
        });
        return;
      }
      setMsg(accept ? "已同意" : "已拒绝");
      load();
    } catch (e) {
      setMsg(e.message);
    }
  };

  if (!userId) {
    return (
      <p className="text-sm">
        请先{" "}
        <Link to="/login" className="text-teal-700 underline">
          登录
        </Link>
      </p>
    );
  }

  const pendingInbox = items.filter((b) => b.status === "pending_claim" && b.dest_user_id === userId);

  return (
    <div className="space-y-5">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex gap-2 text-sm">
          {[
            ["all", "全部"],
            ["sent", "我投递的"],
            ["inbox", "我收到的"],
          ].map(([v, label]) => (
            <button
              key={v}
              type="button"
              onClick={() => setRole(v)}
              className={`px-3 py-1.5 rounded-lg ${
                role === v ? "bg-teal-800 text-white" : "bg-white border"
              }`}
            >
              {label}
            </button>
          ))}
        </div>
        <Link
          to="/planet/bottles/create"
          className="px-4 py-2 rounded-lg bg-teal-700 text-white text-sm"
        >
          投递漂流瓶
        </Link>
      </div>

      {pendingInbox.length > 0 && role !== "sent" && (
        <div className="rounded-xl border border-rose-200 bg-rose-50 px-4 py-3 text-sm text-rose-900">
          有 {pendingInbox.length} 封漂流瓶待领取，点开即可领取并阅读。
        </div>
      )}

      {msg && <p className="text-sm text-rose-600">{msg}</p>}

      {(invites.incoming || []).filter((i) => i.status === "pending").length > 0 && (
        <section className="bg-teal-50 border border-teal-100 rounded-xl p-4 space-y-2">
          <h3 className="text-sm font-medium text-teal-950">待处理的认识邀请</h3>
          {invites.incoming
            .filter((i) => i.status === "pending")
            .map((i) => (
              <div
                key={i.id}
                className="flex flex-wrap items-center justify-between gap-2 text-sm"
              >
                <span>
                  来自用户 {i.from_user_id}
                  {i.bottle_id ? ` · 瓶 #${i.bottle_id}` : ""}
                </span>
                <span className="flex gap-2">
                  <button
                    type="button"
                    className="px-2 py-1 rounded bg-teal-700 text-white"
                    onClick={() => respondInvite(i.id, true)}
                  >
                    同意并私信
                  </button>
                  <button
                    type="button"
                    className="px-2 py-1 rounded border"
                    onClick={() => respondInvite(i.id, false)}
                  >
                    拒绝
                  </button>
                </span>
              </div>
            ))}
        </section>
      )}

      <ul className="space-y-3">
        {items.map((b) => {
          const urgent = b.status === "pending_claim" && b.dest_user_id === userId;
          return (
            <li key={b.id}>
              <Link
                to={`/planet/bottles/${b.id}`}
                className={`block rounded-xl p-4 border hover:border-teal-300 ${
                  urgent
                    ? "bg-rose-50 border-rose-200"
                    : "bg-white border-slate-200"
                }`}
              >
                <div className="flex justify-between gap-3 text-sm">
                  <span className="font-medium text-teal-950">
                    #{b.id} · {b.origin_city || "?"} → {b.dest_city || "待定"}
                  </span>
                  <span className={urgent ? "text-rose-700 font-medium" : "text-slate-500"}>
                    {STATUS_LABEL[b.status] || b.status}
                  </span>
                </div>
                <p className="mt-2 text-sm text-slate-600 line-clamp-2">
                  {b.content_preview || (b.has_image ? "[手写信图片]" : "（无预览）")}
                </p>
                <p className="mt-1 text-xs text-slate-400">
                  {b.deliver_mode} · {b.created_at?.slice(0, 16)?.replace("T", " ")}
                </p>
              </Link>
            </li>
          );
        })}
        {!items.length && (
          <li className="text-sm text-slate-500 py-8 text-center">暂无漂流瓶</li>
        )}
      </ul>
    </div>
  );
}
